import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { useHistory, useLocation } from "react-router-dom";

// services
import taskService, { STATUS_LABELS, PRIORITY_LABELS, STATUS_BG_STYLES, PRIORITY_BG_STYLES, getNextStatusAction, getNextStatusLabel } from "services/tasks.js";
import projectService from "services/projects.js";
import logger from "utils/logger";


const CardTaskTable = forwardRef(({ color, onEditTask, onDeleteTask, onNewTask, onSecretary }, ref) => {
  const history = useHistory();
  const location = useLocation();
  
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projects, setProjects] = useState([]);
  
  // Parse filters from URL query parameters
  const getFiltersFromURL = () => {
    const searchParams = new URLSearchParams(location.search);
    const status = searchParams.get('status');
    const priority = searchParams.get('priority');
    const project_code = searchParams.get('project_code');
    const agent = searchParams.get('agent');
    const order_by = searchParams.get('order_by');

    const filters = {
      status: status ? status.split(',') : ['created', 'pending', 'working', 'reviewing'], // Default to open tasks only
      priority: priority || '',
      project_code: project_code || '',
      agent: agent || '',
      order_by: order_by || 'created_at_desc' // Default to newest first
    };

    // Store filters in localStorage as backup with URL source indicator
    try {
      localStorage.setItem('taskFilters', JSON.stringify({
        ...filters,
        _source: 'url',
        _timestamp: Date.now()
      }));
      if (process.env.NODE_ENV === 'development') {
        console.debug('Filters loaded from URL and stored in localStorage:', filters);
      }
    } catch (error) {
      logger.warn('Could not save filters to localStorage:', error);
    }

    return filters;
  };

  // Get current active filters for API calls
  const getActiveFilters = (currentFilters) => {
    return Object.keys(currentFilters).reduce((acc, key) => {
      if (currentFilters[key]) {
        if (Array.isArray(currentFilters[key])) {
          if (currentFilters[key].length > 0) {
            acc[key] = currentFilters[key];
          }
        } else if (currentFilters[key] !== '') {
          acc[key] = currentFilters[key];
        }
      }
      return acc;
    }, {});
  };
  
  // Update URL with current filters
  const updateURL = (filters) => {
    const searchParams = new URLSearchParams();

    // Add status if not empty
    if (filters.status && filters.status.length > 0) {
      searchParams.set('status', filters.status.join(','));
    }

    // Add priority if not empty
    if (filters.priority && filters.priority !== '') {
      searchParams.set('priority', filters.priority);
    }

    // Add project_code if not empty
    if (filters.project_code && filters.project_code !== '') {
      searchParams.set('project_code', filters.project_code);
    }

    // Add agent if not empty
    if (filters.agent && filters.agent !== '') {
      searchParams.set('agent', filters.agent);
    }

    // Add order_by if not default
    if (filters.order_by && filters.order_by !== 'created_at_desc') {
      searchParams.set('order_by', filters.order_by);
    }

    // Update URL without causing a page refresh
    const newURL = searchParams.toString() ? `${location.pathname}?${searchParams.toString()}` : location.pathname;
    history.replace(newURL);
  };

  // Initialize filters from URL or defaults
  const initializeFilters = () => {
    // If URL has parameters, use them
    if (location.search) {
      return getFiltersFromURL();
    }

    // Otherwise, always use defaults (don't use localStorage to avoid unexpected behavior)
    return {
      status: ['created', 'pending', 'working', 'reviewing'], // Default to open tasks only
      priority: '',
      project_code: '',
      agent: '',
      order_by: 'created_at_desc', // Default to newest first
      include_disabled: false // Default to NOT showing disabled projects
    };
  };

  const [filters, setFilters] = useState(initializeFilters);
  const [rejectingTask, setRejectingTask] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectionError, setRejectionError] = useState('');

  // Load tasks from API
  const loadTasks = async (currentFilters = {}) => {
    try {
      setLoading(true);
      const response = await taskService.getTasks(currentFilters);
      setTasks(response.tasks || response.data || []);
      setError(null);
    } catch (err) {
      logger.error('Error loading tasks:', err);
      setError('Error al cargar las tareas');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  // Load projects from API
  const loadProjects = async (includeDisabled = false) => {
    try {
      const response = await projectService.getProjects({ include_disabled: includeDisabled });
      setProjects(response.data || response.projects || []);
    } catch (err) {
      logger.error('Error loading projects:', err);
      setProjects([]);
    }
  };

  // Initial load
  useEffect(() => {
    // Load tasks with current filters
    loadTasks(filters);
    loadProjects(filters.include_disabled);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Watch for URL changes (browser navigation only)
  useEffect(() => {
    // Only process if we actually have URL parameters to avoid infinite loops
    if (location.search) {
      const filtersFromURL = getFiltersFromURL();
      setFilters(filtersFromURL);

      // Load tasks with filters from URL
      const activeFilters = getActiveFilters(filtersFromURL);
      loadTasks(activeFilters);
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ref to track if we're in the middle of a programmatic URL update
  const isUpdatingURL = React.useRef(false);

  // Ensure URL stays synchronized with filter state (but prevent infinite loops)
  useEffect(() => {
    // Prevent infinite loops by checking if we're already updating
    if (isUpdatingURL.current) {
      isUpdatingURL.current = false;
      return;
    }

    // Only update URL if filters have actually changed and are not from URL initialization
    const currentParams = new URLSearchParams(location.search);
    const newParams = new URLSearchParams();

    // Build expected URL parameters from current filters
    if (filters.status && filters.status.length > 0) {
      newParams.set('status', filters.status.join(','));
    }
    if (filters.priority && filters.priority !== '') {
      newParams.set('priority', filters.priority);
    }
    if (filters.project_code && filters.project_code !== '') {
      newParams.set('project_code', filters.project_code);
    }
    if (filters.agent && filters.agent !== '') {
      newParams.set('agent', filters.agent);
    }
    if (filters.order_by && filters.order_by !== 'created_at_desc') {
      newParams.set('order_by', filters.order_by);
    }

    // Only update if the URL parameters would actually change
    if (currentParams.toString() !== newParams.toString()) {
      isUpdatingURL.current = true;
      updateURL(filters);
    }

    // Debug: Log filter changes in development
    if (process.env.NODE_ENV === 'development') {
      console.debug('Filters updated:', filters);
      console.debug('Current URL:', location.pathname + location.search);
    }
  }, [filters, location.search, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    reloadTasks: () => {
      // Get current active filters
      const activeFilters = getActiveFilters(filters);
      if (process.env.NODE_ENV === 'development') {
        console.debug('Reloading tasks with preserved filters:', activeFilters);
      }
      loadTasks(activeFilters);
    }
  }));

  // Filter change handler
  const handleFilterChange = (filterType, value) => {
    const newFilters = { ...filters, [filterType]: value };

    // Mark that we're about to update URL programmatically to prevent infinite loops
    isUpdatingURL.current = true;

    setFilters(newFilters);

    // Save to localStorage as backup with metadata
    try {
      localStorage.setItem('taskFilters', JSON.stringify({
        ...newFilters,
        _source: 'user_interaction',
        _timestamp: Date.now()
      }));
    } catch (error) {
      logger.warn('Could not save filters to localStorage:', error);
    }

    // Load tasks with active filters
    const activeFilters = getActiveFilters(newFilters);
    loadTasks(activeFilters);

    // If include_disabled changed, reload projects too
    if (filterType === 'include_disabled') {
      loadProjects(value);
    }
  };

  // Handle status multi-select change
  const handleStatusFilterChange = (status) => {
    let newStatusFilter;
    if (filters.status.includes(status)) {
      // Remove status from filter
      newStatusFilter = filters.status.filter(s => s !== status);
    } else {
      // Add status to filter
      newStatusFilter = [...filters.status, status];
    }
    handleFilterChange('status', newStatusFilter);
  };

  // Handle task deletion
  const handleDeleteTask = async (taskId) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar esta tarea?')) {
      try {
        await taskService.deleteTask(taskId);

        // Reload tasks with current filters (URL is already synchronized)
        const activeFilters = getActiveFilters(filters);
        await loadTasks(activeFilters);
        if (onDeleteTask) onDeleteTask(taskId);
      } catch (err) {
        logger.error('Error deleting task:', err);
        alert('Error al eliminar la tarea');
      }
    }
  };

  // Handle advance task status
  const handleAdvanceStatus = async (task) => {
    const actionText = getNextStatusAction(task.status);
    const nextStatusText = getNextStatusLabel(task.status);

    if (window.confirm(`¿Estás seguro de que quieres ${actionText.toLowerCase()} esta tarea? (${nextStatusText})`)) {
      try {
        await taskService.advanceTaskStatus(task.id);

        // Reload tasks with current filters (URL is already synchronized)
        const activeFilters = getActiveFilters(filters);
        await loadTasks(activeFilters);
      } catch (err) {
        logger.error('Error advancing task status:', err);
        alert('Error al actualizar el estado de la tarea');
      }
    }
  };

  const openRejectDialog = (task) => {
    setRejectingTask(task);
    setRejectionReason('');
    setRejectionError('');
  };

  const closeRejectDialog = () => {
    setRejectingTask(null);
    setRejectionReason('');
    setRejectionError('');
  };

  const handleRejectTask = async () => {
    if (!rejectionReason.trim()) {
      setRejectionError('El motivo del rechazo es obligatorio');
      return;
    }
    try {
      await taskService.updateTask(rejectingTask.id, { status: 'rechazada' });
      await taskService.createTaskResponse(rejectingTask.id, {
        content: `MOTIVO DEL RECHAZO: ${rejectionReason.trim()}`
      });
      closeRejectDialog();
      const activeFilters = getActiveFilters(filters);
      await loadTasks(activeFilters);
    } catch (err) {
      logger.error('Error rejecting task:', err);
      alert('Error al rechazar la tarea');
    }
  };

  return (
    <>
      <div
        className={
          "relative flex flex-col min-w-0 break-words w-full shadow-lg rounded " +
          (color === "light" ? "bg-white" : "bg-lightBlue-900 text-white")
        }
      >
        <div className="rounded-t mb-0 px-4 py-3 border-0">
          <div className="flex flex-wrap items-center">
            <div className="relative w-full px-4 max-w-full flex-grow flex-1">
              <h3
                className={
                  "font-semibold text-lg " +
                  (color === "light" ? "text-blueGray-700" : "text-white")
                }
              >
                Gestión de Tareas
              </h3>
            </div>
            <div className="relative w-full px-4 max-w-full flex-grow flex-1 text-right">
              {onNewTask && (
                <button
                  className="bg-indigo-500 text-white active:bg-indigo-600 text-xs font-bold uppercase px-3 py-1 rounded outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                  type="button"
                  onClick={onNewTask}
                >
                  Nueva Tarea
                </button>
              )}
              {onSecretary && (
                <button
                  className="bg-purple-500 text-white active:bg-purple-600 text-xs font-bold uppercase px-3 py-1 rounded outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                  type="button"
                  onClick={onSecretary}
                  title="Crear tarea por voz"
                >
                  <i className="fas fa-microphone mr-1"></i>
                  Secretary
                </button>
              )}
            </div>
          </div>
          
          {/* Filters */}
          <div className="mt-4 space-y-3">
            {/* Top row: Priority, Project, and Sort filters */}
            <div className="flex flex-wrap items-end space-x-4">
              <div className="relative">
                <label className="text-xs font-semibold text-blueGray-500 mb-1 block uppercase">
                  Prioridad
                </label>
                <select
                  value={filters.priority}
                  onChange={(e) => handleFilterChange('priority', e.target.value)}
                  className={
                    "text-sm pr-6 py-2 border rounded outline-none focus:outline-none focus:ring w-48 " +
                    (color === "light" ? "text-blueGray-600 bg-white border-blueGray-300" : "text-white bg-lightBlue-800 border-lightBlue-700")
                  }
                >
                  <option value="">Todas las prioridades</option>
                  <option value="low">Baja</option>
                  <option value="medium">Media</option>
                  <option value="high">Alta</option>
                  <option value="urgent">Urgente</option>
                </select>
              </div>
              <div className="relative">
                <label className="text-xs font-semibold text-blueGray-500 mb-1 block uppercase">
                  Proyecto
                </label>
                <select
                  value={filters.project_code}
                  onChange={(e) => handleFilterChange('project_code', e.target.value)}
                  className={
                    "text-sm pr-6 py-2 border rounded outline-none focus:outline-none focus:ring w-48 " +
                    (color === "light" ? "text-blueGray-600 bg-white border-blueGray-300" : "text-white bg-lightBlue-800 border-lightBlue-700")
                  }
                >
                  <option value="">Todos los proyectos</option>
                  {projects.map(project => (
                    <option key={project.project_code} value={project.project_code}>
                      {project.project_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="relative">
                <label className="text-xs font-semibold text-blueGray-500 mb-1 block uppercase">
                  Agente
                </label>
                <select
                  value={filters.agent}
                  onChange={(e) => handleFilterChange('agent', e.target.value)}
                  className={
                    "text-sm pr-6 py-2 border rounded outline-none focus:outline-none focus:ring w-48 " +
                    (color === "light" ? "text-blueGray-600 bg-white border-blueGray-300" : "text-white bg-lightBlue-800 border-lightBlue-700")
                  }
                >
                  <option value="">Todos los agentes</option>
                  <option value="MAITE">🤖 MAITE (Ingeniero)</option>
                  <option value="TATON">🤖 TATON (Product Owner)</option>
                  <option value="PUA">🤖 PUA (QA)</option>
                  <option value="MELU">🤖 MELU (Marketing)</option>
                  <option value="manual">Manual</option>
                </select>
              </div>
              <div className="relative">
                <label className="text-xs font-semibold text-blueGray-500 mb-1 block uppercase">
                  Ordenar por
                </label>
                <select
                  value={filters.order_by}
                  onChange={(e) => handleFilterChange('order_by', e.target.value)}
                  className={
                    "text-sm pr-6 py-2 border rounded outline-none focus:outline-none focus:ring w-56 " +
                    (color === "light" ? "text-blueGray-600 bg-white border-blueGray-300" : "text-white bg-lightBlue-800 border-lightBlue-700")
                  }
                >
                  <option value="created_at_desc">Fecha: Más Recientes</option>
                  <option value="created_at_asc">Fecha: Más Antiguas</option>
                  <option value="priority_desc">Prioridad: Alta a Baja</option>
                  <option value="priority_asc">Prioridad: Baja a Alta</option>
                  <option value="status">Estado</option>
                  <option value="project">Proyecto</option>
                </select>
              </div>
            </div>

            {/* Bottom row: Status filters horizontal */}
            <div className="mt-3 pt-3 border-t border-blueGray-200">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex flex-wrap items-center gap-4">
                  <label className="flex items-center mr-2">
                    <input
                      type="checkbox"
                      checked={filters.status.includes('created')}
                      onChange={() => handleStatusFilterChange('created')}
                      className="mr-1 rounded border-blueGray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-blueGray-600">Creada</span>
                  </label>
                <label className="flex items-center mr-2">
                  <input
                    type="checkbox"
                    checked={filters.status.includes('pending')}
                    onChange={() => handleStatusFilterChange('pending')}
                    className="mr-1 rounded border-blueGray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-blueGray-600">Pendiente</span>
                </label>
                <label className="flex items-center mr-2">
                  <input
                    type="checkbox"
                    checked={filters.status.includes('working')}
                    onChange={() => handleStatusFilterChange('working')}
                    className="mr-1 rounded border-blueGray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-blueGray-600">En Curso</span>
                </label>
                <label className="flex items-center mr-2">
                  <input
                    type="checkbox"
                    checked={filters.status.includes('reviewing')}
                    onChange={() => handleStatusFilterChange('reviewing')}
                    className="mr-1 rounded border-blueGray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-blueGray-600">En Revisión</span>
                </label>
                <label className="flex items-center mr-2">
                  <input
                    type="checkbox"
                    checked={filters.status.includes('finished')}
                    onChange={() => handleStatusFilterChange('finished')}
                    className="mr-1 rounded border-blueGray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-blueGray-600">Finalizada</span>
                </label>
                  <label className="flex items-center mr-2">
                    <input
                      type="checkbox"
                      checked={filters.status.includes('failed')}
                      onChange={() => handleStatusFilterChange('failed')}
                      className="mr-1 rounded border-blueGray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-blueGray-600">Fallida</span>
                  </label>
                  <label className="flex items-center mr-2">
                    <input
                      type="checkbox"
                      checked={filters.status.includes('rechazada')}
                      onChange={() => handleStatusFilterChange('rechazada')}
                      className="mr-1 rounded border-blueGray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-blueGray-600">Rechazada</span>
                  </label>
                </div>

                {/* Toggle for disabled projects */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginLeft: 'auto',
                  borderLeft: '1px solid #e5e7eb',
                  paddingLeft: '1rem'
                }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    gap: '0.75rem'
                  }}>
                    <span style={{
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      color: '#374151'
                    }}>
                      Mostrar proyectos desactivados
                    </span>
                    <div style={{
                      position: 'relative',
                      width: '44px',
                      height: '24px'
                    }}>
                      <input
                        type="checkbox"
                        checked={filters.include_disabled}
                        onChange={(e) => handleFilterChange('include_disabled', e.target.checked)}
                        style={{
                          opacity: 0,
                          width: 0,
                          height: 0,
                          position: 'absolute'
                        }}
                      />
                      <span style={{
                        position: 'absolute',
                        cursor: 'pointer',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: filters.include_disabled ? '#2563eb' : '#d1d5db',
                        transition: 'background-color 0.2s',
                        borderRadius: '24px'
                      }}>
                        <span style={{
                          position: 'absolute',
                          height: '18px',
                          width: '18px',
                          left: filters.include_disabled ? '23px' : '3px',
                          bottom: '3px',
                          backgroundColor: 'white',
                          transition: 'left 0.2s',
                          borderRadius: '50%'
                        }}></span>
                      </span>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="block w-full overflow-x-auto">
          {/* Tasks table */}
          <table className="items-center w-full bg-transparent border-collapse">
            <thead>
              <tr>
                <th
                  className={
                    "px-3 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-center " +
                    (color === "light"
                      ? "bg-blueGray-50 text-blueGray-500 border-blueGray-100"
                      : "bg-lightBlue-800 text-lightBlue-300 border-lightBlue-700")
                  }
                >
                  ID
                </th>
                <th
                  className={
                    "px-3 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left " +
                    (color === "light"
                      ? "bg-blueGray-50 text-blueGray-500 border-blueGray-100"
                      : "bg-lightBlue-800 text-lightBlue-300 border-lightBlue-700")
                  }
                >
                  Título
                </th>
                <th
                  className={
                    "px-3 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-center " +
                    (color === "light"
                      ? "bg-blueGray-50 text-blueGray-500 border-blueGray-100"
                      : "bg-lightBlue-800 text-lightBlue-300 border-lightBlue-700")
                  }
                >
                  Proyecto
                </th>
                <th
                  className={
                    "px-3 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-center " +
                    (color === "light"
                      ? "bg-blueGray-50 text-blueGray-500 border-blueGray-100"
                      : "bg-lightBlue-800 text-lightBlue-300 border-lightBlue-700")
                  }
                >
                  Estado
                </th>
                <th
                  className={
                    "px-3 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-center " +
                    (color === "light"
                      ? "bg-blueGray-50 text-blueGray-500 border-blueGray-100"
                      : "bg-lightBlue-800 text-lightBlue-300 border-lightBlue-700")
                  }
                >
                  Prioridad
                </th>
                <th
                  className={
                    "px-3 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-center " +
                    (color === "light"
                      ? "bg-blueGray-50 text-blueGray-500 border-blueGray-100"
                      : "bg-lightBlue-800 text-lightBlue-300 border-lightBlue-700")
                  }
                >
                  Agente
                </th>
                <th
                  className={
                    "px-3 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-center " +
                    (color === "light"
                      ? "bg-blueGray-50 text-blueGray-500 border-blueGray-100"
                      : "bg-lightBlue-800 text-lightBlue-300 border-lightBlue-700")
                  }
                >
                  Modelo
                </th>
                <th
                  className={
                    "px-3 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-center " +
                    (color === "light"
                      ? "bg-blueGray-50 text-blueGray-500 border-blueGray-100"
                      : "bg-lightBlue-800 text-lightBlue-300 border-lightBlue-700")
                  }
                >
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan="8" className="px-3 py-4 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900"></div>
                    </div>
                  </td>
                </tr>
              )}

              {error && (
                <tr>
                  <td colSpan="8" className="px-3 py-4 text-center text-red-500">
                    {error}
                  </td>
                </tr>
              )}

              {!loading && !error && tasks.length === 0 && (
                <tr>
                  <td colSpan="8" className="px-3 py-4 text-center text-gray-500">
                    No hay tareas para mostrar
                  </td>
                </tr>
              )}
              
              {!loading && !error && tasks.map((task) => (
                <tr key={task.id}>
                  <td className="border-t-0 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-2 text-center">
                    <span className={color === "light" ? "text-blueGray-600 font-semibold" : "text-white font-semibold"}>
                      {task.id}
                    </span>
                  </td>
                  <td className="border-t-0 align-middle border-l-0 border-r-0 text-xs p-2">
                    <div>
                      <span
                        className={
                          "font-semibold " +
                          (color === "light" ? "text-blueGray-600" : "text-white")
                        }
                        title={task.title}
                      >
                        {task.title.length > 40 ? `${task.title.substring(0, 40)}...` : task.title}
                      </span>
                    </div>
                  </td>
                  <td className="border-t-0 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-2 text-center">
                    <span className={color === "light" ? "text-blueGray-600" : "text-white"}>
                      {task.project ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800">
                          {task.project.project_code}
                        </span>
                      ) : (
                        <span className="text-blueGray-400 italic">Sin proyecto</span>
                      )}
                    </span>
                  </td>
                  <td className="border-t-0 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-2 text-center">
                    <span
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-white"
                      style={STATUS_BG_STYLES[task.status] || { backgroundColor: '#6b7280' }}
                    >
                      {STATUS_LABELS[task.status] || task.status}
                    </span>
                    {task.auto_merge ? (
                      <span
                        className="inline-flex items-center text-green-600 ml-1"
                        title="Auto-merge activado"
                      >
                        <i className="fas fa-code-branch text-lg"></i>
                      </span>
                    ) : ''}
                    {task.relations && task.relations.length > 0 && task.relations.some(rel => rel.related_task && rel.related_task.status !== 'finished') ? (
                      <span
                        className="inline-flex items-center text-orange-600 ml-1"
                        title={`Bloqueada por ${task.relations.filter(rel => rel.related_task && rel.related_task.status !== 'finished').length} tarea(s) predecesora(s) no finalizada(s)`}
                      >
                        <i className="fas fa-link text-lg"></i>
                      </span>
                    ) : ''}
                  </td>
                  <td className="border-t-0 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-2 text-center">
                    <span
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-white"
                      style={PRIORITY_BG_STYLES[task.priority] || { backgroundColor: '#6b7280' }}
                    >
                      {PRIORITY_LABELS[task.priority] || task.priority}
                    </span>
                  </td>
                  <td className="border-t-0 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-2 text-center">
                    <span className={color === "light" ? "text-blueGray-600" : "text-white"}>
                      {task.agent ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-purple-100 text-purple-800">
                          <i className="fas fa-robot mr-1"></i>
                          {task.agent}
                        </span>
                      ) : (
                        <span className="text-blueGray-400 italic">Manual</span>
                      )}
                    </span>
                  </td>
                  <td className="border-t-0 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-2 text-center">
                    {task.ai_model ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-teal-100 text-teal-800">
                        <i className="fas fa-brain mr-1"></i>
                        {task.ai_model.name}
                      </span>
                    ) : (
                      <span className="text-blueGray-400 italic">-</span>
                    )}
                  </td>
                  <td className="border-t-0 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-2 text-center">
                    <div className="flex space-x-1 justify-center">
                      {!['finished', 'failed', 'rechazada'].includes(task.status) && (
                        <button
                          className="text-rose-600 hover:text-rose-900 text-xs font-semibold px-2"
                          onClick={() => openRejectDialog(task)}
                          title="Rechazar tarea"
                        >
                          <i className="fas fa-ban"></i>
                        </button>
                      )}
                      {getNextStatusAction(task.status) && (
                        <button
                          className="text-emerald-600 hover:text-emerald-900 text-xs font-semibold"
                          onClick={() => handleAdvanceStatus(task)}
                          title={`${getNextStatusAction(task.status)} → ${getNextStatusLabel(task.status)}`}
                        >
                          <i className="fas fa-arrow-right"></i>
                        </button>
                      )}
                      <button
                        className="text-indigo-600 hover:text-indigo-900 text-xs font-semibold px-2"
                        onClick={() => onEditTask && onEditTask(task)}
                        title="Editar tarea"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        className="text-red-600 hover:text-red-900 text-xs font-semibold"
                        onClick={() => handleDeleteTask(task.id)}
                        title="Eliminar tarea"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {rejectingTask && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            zIndex: 200000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem'
          }}
          onClick={closeRejectDialog}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '0.5rem',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              width: '100%',
              maxWidth: '480px',
              padding: '1.5rem'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem', gap: '0.75rem' }}>
              <div style={{
                width: '2.5rem', height: '2.5rem',
                borderRadius: '50%',
                backgroundColor: '#fce7f3',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0
              }}>
                <span style={{ fontSize: '1.25rem' }}>🚫</span>
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: '#111827' }}>
                  Rechazar tarea
                </h3>
                <p style={{ margin: 0, fontSize: '0.8rem', color: '#6b7280' }}>
                  #{rejectingTask.id} — {rejectingTask.title.length > 50 ? `${rejectingTask.title.substring(0, 50)}...` : rejectingTask.title}
                </p>
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                Motivo del rechazo *
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => { setRejectionReason(e.target.value); if (e.target.value.trim()) setRejectionError(''); }}
                placeholder="Explica por qué se rechaza esta tarea y qué deben evitar los agentes en el futuro..."
                rows={4}
                autoFocus
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: rejectionError ? '1px solid #dc2626' : '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  resize: 'vertical',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
              {rejectionError && (
                <p style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '0.25rem' }}>{rejectionError}</p>
              )}
              <p style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.35rem' }}>
                Se guardará como comentario con el prefijo "MOTIVO DEL RECHAZO"
              </p>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
              <button
                type="button"
                onClick={closeRejectDialog}
                style={{
                  padding: '0.5rem 1rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: '#374151',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleRejectTask}
                style={{
                  padding: '0.5rem 1.25rem',
                  border: 'none',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: 'white',
                  backgroundColor: '#be185d',
                  cursor: 'pointer'
                }}
              >
                Confirmar rechazo
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
});

CardTaskTable.defaultProps = {
  color: "light",
};

CardTaskTable.propTypes = {
  color: PropTypes.oneOf(["light", "dark"]),
  onEditTask: PropTypes.func,
  onDeleteTask: PropTypes.func,
  onNewTask: PropTypes.func,
  onSecretary: PropTypes.func,
};

export default CardTaskTable;